import { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import ReviewCard from '../components/ReviewCard';
import { Star, Upload, X, Image, ChevronLeft, ChevronRight, Pause, Play, Camera } from 'lucide-react';
import { toast } from 'sonner';

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    rating: 5,
    comment: '',
    images: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const slideInterval = useRef(null);

  // Company gallery state
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [currentGalleryPhoto, setCurrentGalleryPhoto] = useState(0);
  const [galleryFadeState, setGalleryFadeState] = useState('in');

  // Auto-scroll functionality for reviews
  const nextSlide = useCallback(() => {
    if (reviews.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % reviews.length);
    }
  }, [reviews.length]);

  const prevSlide = () => {
    if (reviews.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + reviews.length) % reviews.length);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Auto-play effect for reviews carousel
  useEffect(() => {
    if (isPlaying && reviews.length > 1) {
      slideInterval.current = setInterval(nextSlide, 5000); // Change slide every 5 seconds
    }
    return () => {
      if (slideInterval.current) {
        clearInterval(slideInterval.current);
      }
    };
  }, [isPlaying, reviews.length, nextSlide]);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  // Gallery photo fade rotation
  const rotateGalleryPhoto = useCallback(() => {
    if (galleryPhotos.length > 1) {
      setGalleryFadeState('out');
      setTimeout(() => {
        setCurrentGalleryPhoto((prev) => (prev + 1) % galleryPhotos.length);
        setGalleryFadeState('in');
      }, 500);
    }
  }, [galleryPhotos.length]);

  useEffect(() => {
    if (galleryPhotos.length > 1) {
      const interval = setInterval(rotateGalleryPhoto, 4000); // Change every 4 seconds
      return () => clearInterval(interval);
    }
  }, [galleryPhotos.length, rotateGalleryPhoto]);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchReviews();
    fetchGalleryPhotos();
  }, []);

  const fetchGalleryPhotos = async () => {
    try {
      const photos = await api.getGalleryPhotos();
      setGalleryPhotos(photos);
    } catch (error) {
      console.error('Error fetching gallery photos:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      await api.seedData();
      const data = await api.getReviews();
      setReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_name.trim() || !formData.comment.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.createReview(formData);
      toast.success('Thank you for your review!');
      setFormData({ customer_name: '', rating: 5, comment: '', images: [] });
      setShowForm(false);
      fetchReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limit to 3 images
    if (formData.images.length + files.length > 3) {
      toast.error('Maximum 3 images allowed per review');
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        const result = await api.uploadImage(file);
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, { url: result.url, alt: 'Customer review image' }]
        }));
      }
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const averageRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div data-testid="reviews-page" className="pt-20 min-h-screen bg-void">
      {/* Header */}
      <div className="py-16 md:py-24 px-6 md:px-12 lg:px-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
            Testimonials
          </span>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter uppercase text-white mb-4">
            Customer Reviews
          </h1>
          <div className="flex items-center gap-4">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-5 h-5 ${
                    i < Math.round(parseFloat(averageRating))
                      ? 'fill-cyan-400 text-cyan-400'
                      : 'text-neutral-700'
                  }`}
                />
              ))}
            </div>
            <span className="text-white font-mono">{averageRating}</span>
            <span className="text-neutral-400">({reviews.length} reviews)</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Add Review Button */}
          <div className="mb-12">
            {!showForm ? (
              <button
                data-testid="write-review-btn"
                onClick={() => setShowForm(true)}
                className="bg-cyan-400 text-white px-8 py-4 font-mono uppercase tracking-widest text-sm hover:bg-cyan-400-hover transition-all duration-300 hover:shadow-[0_0_20px_rgba(217,70,239,0.4)]"
              >
                Write a Review
              </button>
            ) : (
              <div className="max-w-2xl bg-void-paper border border-white/10 p-8">
                <h3 className="font-heading text-xl font-semibold text-white mb-6">
                  Share Your Experience
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      data-testid="review-name-input"
                      value={formData.customer_name}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, customer_name: e.target.value }))
                      }
                      placeholder="Enter your name"
                      className="w-full bg-void border border-white/10 h-12 px-4 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                      Rating
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          data-testid={`rating-star-${star}`}
                          onClick={() => setFormData((prev) => ({ ...prev, rating: star }))}
                          className="p-1"
                        >
                          <Star
                            className={`w-8 h-8 transition-colors ${
                              star <= formData.rating
                                ? 'fill-cyan-400 text-cyan-400'
                                : 'text-neutral-700 hover:text-neutral-500'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                      Your Review
                    </label>
                    <textarea
                      data-testid="review-comment-input"
                      value={formData.comment}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, comment: e.target.value }))
                      }
                      placeholder="Share your thoughts about the product..."
                      rows={4}
                      className="w-full bg-void border border-white/10 p-4 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors resize-none"
                    />
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                      Add Photos (Optional)
                    </label>
                    <p className="text-xs text-neutral-500 mb-3">
                      Share photos of your purchase! Max 3 images.
                    </p>
                    
                    {/* Image Preview Grid */}
                    {formData.images.length > 0 && (
                      <div className="flex gap-3 mb-4 flex-wrap">
                        {formData.images.map((img, index) => (
                          <div key={img.url || `preview-${index}`} className="relative group">
                            <img
                              src={img.url}
                              alt={img.alt}
                              className="w-24 h-24 object-cover border border-white/10"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-4 h-4 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Upload Button */}
                    {formData.images.length < 3 && (
                      <label className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-neutral-400 hover:text-white hover:border-cyan-400/50 cursor-pointer transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploading}
                        />
                        {uploading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-cyan-400/20 border-t-cyan-400 rounded-full animate-spin" />
                            <span className="text-sm font-mono uppercase tracking-widest">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Image className="w-4 h-4" />
                            <span className="text-sm font-mono uppercase tracking-widest">Add Photo</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      data-testid="submit-review-btn"
                      disabled={submitting}
                      className="bg-cyan-400 text-white px-8 py-3 font-mono uppercase tracking-widest text-sm hover:bg-cyan-400-hover transition-colors disabled:opacity-50"
                    >
                      {submitting ? 'Submitting...' : 'Submit Review'}
                    </button>
                    <button
                      type="button"
                      data-testid="cancel-review-btn"
                      onClick={() => setShowForm(false)}
                      className="text-neutral-400 hover:text-white px-4 py-3 font-mono uppercase tracking-widest text-sm transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Reviews Carousel */}
          {loading ? (
            <div className="flex justify-center">
              <div className="w-full max-w-2xl h-64 bg-void-subtle animate-pulse" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-neutral-400 font-mono text-sm uppercase tracking-widest">
                No reviews yet. Be the first to share your experience!
              </p>
            </div>
          ) : (
            <div className="relative">
              {/* Carousel Container */}
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {reviews.map((review) => (
                    <div 
                      key={review.id} 
                      className="w-full flex-shrink-0 px-4 md:px-16"
                    >
                      <div className="max-w-2xl mx-auto">
                        <ReviewCard review={review} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Arrows */}
              {reviews.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-void-paper/80 border border-white/10 text-white hover:bg-cyan-400/20 hover:border-cyan-400/50 transition-all"
                    aria-label="Previous review"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-void-paper/80 border border-white/10 text-white hover:bg-cyan-400/20 hover:border-cyan-400/50 transition-all"
                    aria-label="Next review"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Dots Navigation & Play/Pause */}
              {reviews.length > 1 && (
                <div className="flex items-center justify-center gap-4 mt-8">
                  {/* Play/Pause Button */}
                  <button
                    onClick={togglePlayPause}
                    className="w-10 h-10 flex items-center justify-center border border-white/20 text-neutral-400 hover:text-cyan-400 hover:border-cyan-400/50 transition-all"
                    aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>

                  {/* Dots */}
                  <div className="flex gap-2">
                    {reviews.map((review, index) => (
                      <button
                        key={review.id || `dot-${index}`}
                        onClick={() => goToSlide(index)}
                        className={`w-3 h-3 rounded-full transition-all ${
                          currentSlide === index
                            ? 'bg-cyan-400 w-8'
                            : 'bg-white/20 hover:bg-white/40'
                        }`}
                        aria-label={`Go to review ${index + 1}`}
                      />
                    ))}
                  </div>

                  {/* Counter */}
                  <span className="text-neutral-500 font-mono text-sm">
                    {currentSlide + 1} / {reviews.length}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Company Gallery Section */}
          {galleryPhotos.length > 0 && (
            <div className="mt-24 pt-16 border-t border-white/5">
              <div className="text-center mb-12">
                <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
                  Behind the Scenes
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-white flex items-center justify-center gap-3">
                  <Camera className="w-8 h-8 text-cyan-400" />
                  Only Skullz Gallery
                </h2>
              </div>

              {/* Fade in/out Gallery */}
              <div className="relative max-w-4xl mx-auto">
                <div 
                  className={`transition-opacity duration-500 ease-in-out ${
                    galleryFadeState === 'in' ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <div className="aspect-video bg-void-paper border border-white/10 overflow-hidden">
                    <img
                      src={galleryPhotos[currentGalleryPhoto]?.url}
                      alt={galleryPhotos[currentGalleryPhoto]?.caption || 'Only Skullz gallery'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {galleryPhotos[currentGalleryPhoto]?.caption && (
                    <p className="text-center text-neutral-400 mt-4 italic">
                      {galleryPhotos[currentGalleryPhoto].caption}
                    </p>
                  )}
                </div>

                {/* Gallery dot indicators */}
                {galleryPhotos.length > 1 && (
                  <div className="flex justify-center gap-2 mt-6">
                    {galleryPhotos.map((photo, index) => (
                      <button
                        key={photo.url || `gallery-dot-${index}`}
                        onClick={() => {
                          setGalleryFadeState('out');
                          setTimeout(() => {
                            setCurrentGalleryPhoto(index);
                            setGalleryFadeState('in');
                          }, 500);
                        }}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          currentGalleryPhoto === index
                            ? 'bg-cyan-400 w-6'
                            : 'bg-white/20 hover:bg-white/40'
                        }`}
                        aria-label={`Go to photo ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewsPage;
